import { FC } from 'react';
import { Box, IconButton, Stack, Typography, Divider } from '@mui/material';
import { Query } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { type ArtworkDetail, ArtworkTypeEnum } from '@api';
import { useGetArtwork, TinPreview } from '@core/features/artworks';
import { useBasketStore } from '../stores';
import { useBasketLimit } from '../hooks';
import { PlusIcon, MinusIcon, CloseIcon } from '@ui/icons';

type Props = {
  artworkId: string;
  quantity: number;
};

/**
 * One row in the BasketDialog. Renders a TinPreview for the artwork, a
 * quantity stepper (- N +), and a delete icon. Polls the artwork query
 * until a thumbnail is available (matches the pattern from the retired
 * ReviewArtworkStep).
 */
export const BasketPositionRow: FC<Props> = ({ artworkId, quantity }) => {
  const { t } = useTranslation();
  const { updateQuantity, removePosition } = useBasketStore();
  const { maxOrderQuantity, isLimitReached } = useBasketLimit();

  const { data } = useGetArtwork(artworkId, {
    refetchInterval: (query: Query) => {
      const current = query.state.data as ArtworkDetail | undefined;
      return current?.thumbnail ? false : 2000;
    },
    refetchOnWindowFocus: false,
  });

  const label =
    data?.artwork_type === ArtworkTypeEnum.PHOTO
      ? t('basketPhotoTin')
      : data?.artwork_type === ArtworkTypeEnum.LOGO
        ? t('basketLogoTin')
        : '';

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      useFlexGap
      sx={{
        padding: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ width: 72, height: 72, flexShrink: 0 }}>
        <TinPreview image={data?.thumbnail ?? ''} />
      </Box>

      <Stack direction="column" sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{label}</Typography>
        {data?.artwork_type === ArtworkTypeEnum.LOGO && data.logo_text && (
          <Typography
            sx={{ fontSize: 14, fontWeight: 400, color: 'text.secondary' }}
          >
            {data.logo_text}
          </Typography>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton
          color="secondary"
          onClick={() => updateQuantity(artworkId, quantity - 1)}
          aria-label="decrement"
        >
          <MinusIcon />
        </IconButton>
        <Typography sx={{ minWidth: 24, textAlign: 'center' }}>
          {quantity}
        </Typography>
        <IconButton
          color="secondary"
          onClick={() =>
            updateQuantity(artworkId, quantity + 1, maxOrderQuantity)
          }
          disabled={isLimitReached}
          aria-label="increment"
        >
          <PlusIcon />
        </IconButton>
      </Stack>
      <Divider
        orientation="vertical"
        flexItem
        variant="middle"
        sx={{ mx: 4 }}
      />
      <IconButton
        color="secondary"
        onClick={() => removePosition(artworkId)}
        aria-label={t('basketRemovePosition')}
        sx={{ mr: 4 }}
      >
        <CloseIcon />
      </IconButton>
    </Stack>
  );
};
